/**
 * Pure matching logic: (full IP → drawing name) items → CCTV graph elements.
 *
 * Unlike cctv-ip-import (which needs coordinate cloud alignment), this match
 * is exact: a placed camera already carries `properties.ipAddress`, so we just
 * key elements by IP and look items up. JointJS-agnostic — accepts the same
 * minimal `CctvElementLike` shape as cctv-ip-import so unit tests can inject
 * plain mocks.
 */

import type { CctvElementLike } from '../../cctv-ip-import'
import type {
  CctvNameItem,
  CctvNameMatchResult,
  CctvNameUpdate,
} from '../model/types'

/** Read the ipAddress a placed element carries, if any. */
function elementIp(el: CctvElementLike): string | null {
  const data = el.get('data')
  const props = (data?.properties ?? {}) as Record<string, unknown>
  const ip = props.ipAddress
  return typeof ip === 'string' && ip.trim() !== '' ? ip.trim() : null
}

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/

/**
 * Infer the site IP prefix (first two octets, e.g. `172.19`) from placed
 * cameras' ipAddress values by majority vote. The backend composes PDF IP
 * labels (last two octets) with this prefix — deriving it from the cameras
 * avoids requiring the separate 'CCTV IP 앞자리' site setting when IPs are
 * already mapped. Returns null when no camera carries a valid IPv4.
 */
export function dominantIpPrefix(elements: CctvElementLike[]): string | null {
  const counts = new Map<string, number>()
  for (const el of elements) {
    const ip = elementIp(el)
    if (!ip) continue
    const m = IPV4_RE.exec(ip)
    if (!m) continue
    const prefix = `${m[1]}.${m[2]}`
    counts.set(prefix, (counts.get(prefix) ?? 0) + 1)
  }
  let best: string | null = null
  let bestCount = 0
  for (const [prefix, count] of counts) {
    if (count > bestCount) {
      best = prefix
      bestCount = count
    }
  }
  return best
}

/**
 * Match mapping items to elements by exact ipAddress equality.
 *
 * Elements without an ipAddress are ignored (the drawing name can only be
 * routed through the IP). If two placed elements somehow share an IP, the
 * name is applied to both — duplicated IPs are a data problem the IP import
 * already guards against, and renaming both is the least surprising outcome.
 */
export function matchCctvNamesToElements(
  items: CctvNameItem[],
  elements: CctvElementLike[]
): CctvNameMatchResult {
  const byIp = new Map<string, CctvElementLike[]>()
  for (const el of elements) {
    const ip = elementIp(el)
    if (!ip) continue
    const list = byIp.get(ip)
    if (list) {
      list.push(el)
    } else {
      byIp.set(ip, [el])
    }
  }

  const updates: CctvNameUpdate[] = []
  const unmatchedIps: string[] = []
  let matched = 0
  for (const item of items) {
    const targets = byIp.get(item.ip)
    if (!targets || targets.length === 0) {
      unmatchedIps.push(item.ip)
      continue
    }
    matched += 1
    for (const el of targets) {
      updates.push({ id: String(el.id), name: item.name })
    }
  }

  return {
    updates,
    matched,
    totalItems: items.length,
    cctvWithIpCount: byIp.size,
    unmatchedIps,
  }
}
